import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    adminLogout,
    adminMe,
    createContent,
    deleteContent,
    listContent,
    updateContent,
    uploadImage
} from '../services/adminApi';
import './Admin.css';

/* ============================================================
   MODULE DEFINITIONS — icons, descriptions, field schemas
   ============================================================ */
const MODULES = [
    {
        key: 'tours',
        label: 'Tours',
        icon: 'fas fa-route',
        description: 'Adventure tour packages with itinerary and pricing.',
        previewType: 'tour'
    },
    {
        key: 'packages',
        label: 'Packages',
        icon: 'fas fa-tags',
        description: 'Pricing plans and curated travel bundles.',
        previewType: 'package'
    },
    {
        key: 'blogs',
        label: 'Blog Posts',
        icon: 'fas fa-newspaper',
        description: 'Travel stories, guides and news articles.',
        previewType: 'blog'
    },
    {
        key: 'services',
        label: 'Services',
        icon: 'fas fa-concierge-bell',
        description: 'Services offered to travellers.',
        previewType: 'service'
    },
    {
        key: 'destinations',
        label: 'Destinations',
        icon: 'fas fa-mountain-sun',
        description: 'Featured travel destinations across the region.',
        previewType: 'destination'
    },
    {
        key: 'activities',
        label: 'Activities',
        icon: 'fas fa-person-hiking',
        description: 'Adventure activities like trekking, climbing, rafting.',
        previewType: 'activity'
    },
    {
        key: 'faqs',
        label: 'FAQs',
        icon: 'fas fa-circle-question',
        description: 'Frequently asked questions for visitors.',
        previewType: 'faq'
    }
];

const DEFAULT_FORM = {
    id: null,
    title: '',
    slug: '',
    summary: '',
    content: '',
    image: '',
    duration: '',
    price: '',
    status: 'published',
    // payload (module-specific)
    location: '',
    groupSize: '',
    rating: '',
    category: '',
    author: '',
    excerpt: '',
    features: '',
    badge: '',
    icon: ''
};

/* ============================================================
   HELPERS
   ============================================================ */
const slugify = (s) =>
    String(s || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

const buildPayload = (form) => {
    const payload = {};
    const add = (key, val) => {
        if (val !== undefined && val !== null && String(val).trim() !== '') {
            payload[key] = val;
        }
    };

    add('location', form.location);
    add('groupSize', form.groupSize);
    add('rating', form.rating);
    add('category', form.category);
    add('author', form.author);
    add('excerpt', form.excerpt);
    add('badge', form.badge);
    add('icon', form.icon);

    if (form.features && form.features.trim()) {
        payload.features = form.features
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
    }

    return JSON.stringify(payload);
};

const parsePayload = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try {
        return JSON.parse(raw);
    } catch {
        return {};
    }
};

/* ============================================================
   LIVE PREVIEW COMPONENTS
   ============================================================ */
function ImageOrPlaceholder({ image, height = 200 }) {
    if (image) {
        return (
            <div
                className="admin-pv-img"
                style={{ height, backgroundImage: `url(${image})` }}
            />
        );
    }
    return (
        <div className="admin-pv-img" style={{ height }}>
            <div className="admin-pv-img-placeholder">
                <i className="fas fa-image"></i>
                Add an image to see preview
            </div>
        </div>
    );
}

function PreviewPane({ form, moduleType }) {
    const title = form.title || 'Your title appears here';
    const summary = form.summary || 'A short summary of this item will be shown to visitors here. Write something engaging to attract bookings.';
    const content = form.content || '';

    if (moduleType === 'blog') {
        return (
            <div className="admin-preview-frame">
                <span className="admin-preview-tag">Live Preview · Blog Card</span>
                <div className="admin-pv-card">
                    <ImageOrPlaceholder image={form.image} />
                    <div className="admin-pv-body">
                        <div className="admin-pv-meta">
                            {form.category && <span><i className="fas fa-folder"></i> {form.category}</span>}
                            {form.author && <span><i className="fas fa-user"></i> {form.author}</span>}
                        </div>
                        <h3 className="admin-pv-title">{title}</h3>
                        <p className="admin-pv-summary">{form.excerpt || summary}</p>
                        <div className="admin-pv-divider"></div>
                        <div className="admin-pv-footer">
                            <div className="admin-pv-author">
                                <div className="admin-pv-author-avatar">
                                    {(form.author || 'H').charAt(0).toUpperCase()}
                                </div>
                                <span>{form.author || 'Himolan Team'}</span>
                            </div>
                            <span className="admin-pv-btn">Read more</span>
                        </div>
                    </div>
                </div>
                {content && (
                    <div className="admin-pv-content">{content}</div>
                )}
            </div>
        );
    }

    if (moduleType === 'package') {
        const features = (form.features || '').split('\n').map((s) => s.trim()).filter(Boolean);
        return (
            <div className="admin-preview-frame">
                <span className="admin-preview-tag">Live Preview · Package</span>
                <div className="admin-pv-card">
                    <ImageOrPlaceholder image={form.image} height={160} />
                    <div className="admin-pv-body">
                        {form.badge && (
                            <div style={{ marginBottom: 8 }}>
                                <span className="admin-pill published">{form.badge}</span>
                            </div>
                        )}
                        <h3 className="admin-pv-title">{title}</h3>
                        <p className="admin-pv-summary">{summary}</p>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                            <span style={{ fontSize: 28, fontWeight: 800, color: '#113D48' }}>
                                {form.price ? (form.price.toString().startsWith('$') || form.price.toString().includes('PKR') ? form.price : `$${form.price}`) : '$0'}
                            </span>
                            <span style={{ color: '#6E7E85', fontSize: 13 }}>/ person</span>
                        </div>
                        {features.length > 0 && (
                            <div className="admin-pv-features">
                                {features.map((f, i) => (
                                    <div key={i}>
                                        <i className="fas fa-check"></i>
                                        {f}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="admin-pv-divider"></div>
                        <div className="admin-pv-btn" style={{ display: 'inline-block' }}>Book this package</div>
                    </div>
                </div>
            </div>
        );
    }

    if (moduleType === 'service' || moduleType === 'activity' || moduleType === 'destination') {
        return (
            <div className="admin-preview-frame">
                <span className="admin-preview-tag">Live Preview · {moduleType}</span>
                <div className="admin-pv-card">
                    <ImageOrPlaceholder image={form.image} height={180} />
                    <div className="admin-pv-body">
                        <div className="admin-pv-meta">
                            {form.location && <span><i className="fas fa-location-dot"></i> {form.location}</span>}
                            {form.duration && <span><i className="fas fa-clock"></i> {form.duration}</span>}
                            {form.rating && <span><i className="fas fa-star" style={{ color: '#FFB539' }}></i> {form.rating}</span>}
                        </div>
                        <h3 className="admin-pv-title">{title}</h3>
                        <p className="admin-pv-summary">{summary}</p>
                        {form.price && (
                            <div style={{ color: '#1CA8CB', fontWeight: 800, fontSize: 18 }}>
                                {form.price.toString().includes('$') || form.price.toString().includes('PKR') ? form.price : `From $${form.price}`}
                            </div>
                        )}
                        <div className="admin-pv-divider"></div>
                        <div className="admin-pv-btn" style={{ display: 'inline-block' }}>Explore</div>
                    </div>
                </div>
            </div>
        );
    }

    if (moduleType === 'faq') {
        return (
            <div className="admin-preview-frame">
                <span className="admin-preview-tag">Live Preview · FAQ</span>
                <div className="admin-pv-faq-q">{title}</div>
                <div className="admin-pv-faq-a">{summary || content || 'Your answer will appear here.'}</div>
            </div>
        );
    }

    // Default: tour
    return (
        <div className="admin-preview-frame">
            <span className="admin-preview-tag">Live Preview · Tour Card</span>
            <div className="admin-pv-card">
                <ImageOrPlaceholder image={form.image} />
                {form.price && (
                    <div style={{ position: 'relative' }}>
                        <span className="admin-pv-price-tag" style={{ position: 'absolute', top: -190, right: 14 }}>
                            {form.price.toString().includes('$') || form.price.toString().includes('PKR') ? form.price : `$${form.price}`}
                        </span>
                    </div>
                )}
                <div className="admin-pv-body">
                    <div className="admin-pv-meta">
                        {form.location && <span><i className="fas fa-location-dot"></i> {form.location}</span>}
                        {form.duration && <span><i className="fas fa-clock"></i> {form.duration}</span>}
                        {form.groupSize && <span><i className="fas fa-users"></i> {form.groupSize}</span>}
                        {form.rating && <span><i className="fas fa-star" style={{ color: '#FFB539' }}></i> {form.rating}</span>}
                    </div>
                    <h3 className="admin-pv-title">{title}</h3>
                    <p className="admin-pv-summary">{summary}</p>
                    <div className="admin-pv-divider"></div>
                    <div className="admin-pv-footer">
                        <span style={{ color: '#6E7E85', fontSize: 13 }}>
                            {form.category || 'Adventure Tour'}
                        </span>
                        <span className="admin-pv-btn">Book Now</span>
                    </div>
                </div>
            </div>
            {content && <div className="admin-pv-content">{content}</div>}
        </div>
    );
}

/* ============================================================
   MODULE-SPECIFIC FORM FIELDS
   ============================================================ */
function ModuleFields({ moduleKey, form, onChange }) {
    if (moduleKey === 'blogs') {
        return (
            <>
                <div className="admin-form-row">
                    <div className="admin-field">
                        <label>Author</label>
                        <input name="author" value={form.author} onChange={onChange} placeholder="e.g. Ahmed Khan" />
                    </div>
                    <div className="admin-field">
                        <label>Category</label>
                        <input name="category" value={form.category} onChange={onChange} placeholder="e.g. Adventure" />
                    </div>
                </div>
                <div className="admin-field">
                    <label>Excerpt (short teaser)</label>
                    <textarea name="excerpt" value={form.excerpt} rows={2} onChange={onChange}
                        placeholder="A short teaser shown on blog list cards." />
                </div>
            </>
        );
    }

    if (moduleKey === 'packages') {
        return (
            <>
                <div className="admin-form-row">
                    <div className="admin-field">
                        <label>Price</label>
                        <input name="price" value={form.price} onChange={onChange} placeholder="e.g. 499 or $499" />
                    </div>
                    <div className="admin-field">
                        <label>Badge (optional)</label>
                        <input name="badge" value={form.badge} onChange={onChange} placeholder="e.g. Popular, Best Value" />
                    </div>
                </div>
                <div className="admin-field">
                    <label>Features (one per line)</label>
                    <textarea name="features" value={form.features} rows={4} onChange={onChange}
                        placeholder={'Hotel pickup & drop\nProfessional guide\nAll meals included\nPhotography session'} />
                    <span className="admin-field-hint">Each line becomes a checkmark feature in the package card.</span>
                </div>
            </>
        );
    }

    if (moduleKey === 'faqs') {
        return null;
    }

    // tours / services / destinations / activities
    return (
        <>
            <div className="admin-form-row-3">
                <div className="admin-field">
                    <label>Location</label>
                    <input name="location" value={form.location} onChange={onChange} placeholder="e.g. Skardu, GB" />
                </div>
                <div className="admin-field">
                    <label>Duration</label>
                    <input name="duration" value={form.duration} onChange={onChange} placeholder="e.g. 5 Days / 4 Nights" />
                </div>
                <div className="admin-field">
                    <label>Price</label>
                    <input name="price" value={form.price} onChange={onChange} placeholder="e.g. 499 or PKR 50000" />
                </div>
            </div>
            <div className="admin-form-row-3">
                <div className="admin-field">
                    <label>Group Size</label>
                    <input name="groupSize" value={form.groupSize} onChange={onChange} placeholder="e.g. Up to 12" />
                </div>
                <div className="admin-field">
                    <label>Rating</label>
                    <input name="rating" value={form.rating} onChange={onChange} placeholder="e.g. 4.9" />
                </div>
                <div className="admin-field">
                    <label>Category</label>
                    <input name="category" value={form.category} onChange={onChange} placeholder="e.g. Trekking" />
                </div>
            </div>
        </>
    );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
function AdminDashboard() {
    const navigate = useNavigate();
    const [authChecking, setAuthChecking] = useState(true);
    const [activeModule, setActiveModule] = useState('tours');
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [adminUser, setAdminUser] = useState(null);

    const activeModuleDef = useMemo(
        () => MODULES.find((m) => m.key === activeModule) || MODULES[0],
        [activeModule]
    );

    /* ---------- Auth check ---------- */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await adminMe();
                if (!cancelled) {
                    setAdminUser(result.admin || result);
                    setAuthChecking(false);
                }
            } catch {
                if (!cancelled) navigate('/admin/login', { replace: true });
            }
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    /* ---------- Load items on module change ---------- */
    useEffect(() => {
        if (authChecking) return;
        setForm(DEFAULT_FORM);
        setMessage('');
        setError('');
        loadItems(activeModule);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeModule, authChecking]);

    const loadItems = async (moduleName) => {
        setLoading(true);
        try {
            const data = await listContent(moduleName);
            setItems(data.items || []);
        } catch (e) {
            setError(e.message || 'Failed to load records.');
        } finally {
            setLoading(false);
        }
    };

    /* ---------- Field handlers ---------- */
    const handleFieldChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            // Auto-slug from title if slug empty
            if (name === 'title' && !prev.slug) {
                next.slug = slugify(value);
            }
            return next;
        });
    };

    const handleSlugChange = (event) => {
        setForm((prev) => ({ ...prev, slug: slugify(event.target.value) }));
    };

    const handleStatus = (status) => {
        setForm((prev) => ({ ...prev, status }));
    };

    /* ---------- Image upload ---------- */
    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setError('');
        setUploading(true);
        try {
            const result = await uploadImage(file);
            setForm((prev) => ({ ...prev, image: result.url }));
            setMessage('Image uploaded successfully.');
        } catch (e) {
            setError(e.message || 'Upload failed.');
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const handleClearImage = () => {
        setForm((prev) => ({ ...prev, image: '' }));
    };

    /* ---------- Edit / new ---------- */
    const handleEdit = (item) => {
        const extra = parsePayload(item.payload);
        setForm({
            id: item.id,
            title: item.title || '',
            slug: item.slug || '',
            summary: item.summary || '',
            content: item.content || '',
            image: item.image || '',
            duration: item.duration || '',
            price: item.price || '',
            status: item.status || 'published',
            location: extra.location || '',
            groupSize: extra.groupSize || '',
            rating: extra.rating || '',
            category: extra.category || '',
            author: extra.author || '',
            excerpt: extra.excerpt || '',
            features: Array.isArray(extra.features) ? extra.features.join('\n') : (extra.features || ''),
            badge: extra.badge || '',
            icon: extra.icon || ''
        });
        setMessage(`Editing: ${item.title}`);
        setError('');
        // Scroll to top of form on mobile
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetForm = () => {
        setForm(DEFAULT_FORM);
        setMessage('');
        setError('');
    };

    /* ---------- Save ---------- */
    const handleSave = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        setMessage('');

        try {
            const payload = {
                module: activeModule,
                id: form.id,
                title: form.title.trim(),
                slug: form.slug.trim() || slugify(form.title),
                summary: form.summary.trim(),
                content: form.content.trim(),
                image: form.image.trim(),
                duration: form.duration.trim(),
                price: String(form.price).trim(),
                status: form.status,
                payload: buildPayload(form)
            };

            if (form.id) {
                await updateContent(payload);
                setMessage('Record updated successfully.');
            } else {
                await createContent(payload);
                setMessage('Record created successfully.');
                setForm(DEFAULT_FORM);
            }
            loadItems(activeModule);
        } catch (e) {
            setError(e.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Delete "${title}" permanently?`)) return;
        try {
            await deleteContent(id);
            setMessage('Record deleted.');
            if (form.id === id) resetForm();
            loadItems(activeModule);
        } catch (e) {
            setError(e.message || 'Delete failed.');
        }
    };

    const handleLogout = async () => {
        try { await adminLogout(); } finally {
            navigate('/admin/login', { replace: true });
        }
    };

    /* ---------- Stats ---------- */
    const stats = useMemo(() => {
        const published = items.filter((i) => i.status === 'published').length;
        const draft = items.filter((i) => i.status === 'draft').length;
        const withImage = items.filter((i) => i.image).length;
        return { total: items.length, published, draft, withImage };
    }, [items]);

    if (authChecking) {
        return (
            <div className="admin-loading">
                <div className="admin-spinner"></div>
                <span>Verifying admin session...</span>
            </div>
        );
    }

    const adminInitial = (adminUser?.username || 'A').charAt(0).toUpperCase();

    return (
        <div className="admin-dashboard">
            {sidebarOpen && (
                <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
            )}

            <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="admin-sidebar-brand">
                    <img
                        className="admin-sidebar-brand-logo"
                        src={`${process.env.PUBLIC_URL}/assets/img/logo-white.png`}
                        alt="Himolan Peak Adventures"
                    />
                </div>

                <div className="admin-sidebar-section-label">Content</div>
                <nav>
                    {MODULES.map((m) => (
                        <button
                            key={m.key}
                            type="button"
                            className={activeModule === m.key ? 'active' : ''}
                            onClick={() => { setActiveModule(m.key); setSidebarOpen(false); }}
                        >
                            <i className={m.icon}></i>
                            {m.label}
                        </button>
                    ))}
                </nav>

                <div className="admin-sidebar-footer">
                    <div className="admin-sidebar-user">
                        <div className="admin-sidebar-user-avatar">{adminInitial}</div>
                        <div className="admin-sidebar-user-info">
                            <strong>{adminUser?.username || 'Admin'}</strong>
                            <span>Signed in</span>
                        </div>
                    </div>
                    <button type="button" className="admin-logout" onClick={handleLogout}>
                        <i className="fas fa-arrow-right-from-bracket"></i>
                        Logout
                    </button>
                </div>
            </aside>

            <main className="admin-main">
                {/* Top bar */}
                <div className="admin-topbar">
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <button
                            type="button"
                            className="admin-mobile-menu"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open menu"
                        >
                            <i className="fas fa-bars"></i>
                        </button>
                        <div className="admin-topbar-left">
                            <h1>
                                <i className={activeModuleDef.icon} style={{ color: '#1CA8CB' }}></i>
                                {activeModuleDef.label}
                                <span className="admin-module-badge">{stats.total} items</span>
                            </h1>
                            <p>{activeModuleDef.description}</p>
                        </div>
                    </div>
                    <div className="admin-topbar-right">
                        <button type="button" className="admin-btn secondary" onClick={resetForm}>
                            <i className="fas fa-plus"></i> New
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="admin-stats">
                    <div className="admin-stat">
                        <div className="admin-stat-icon"><i className="fas fa-database"></i></div>
                        <div className="admin-stat-info">
                            <strong>{stats.total}</strong>
                            <span>Total Records</span>
                        </div>
                    </div>
                    <div className="admin-stat">
                        <div className="admin-stat-icon"><i className="fas fa-eye"></i></div>
                        <div className="admin-stat-info">
                            <strong>{stats.published}</strong>
                            <span>Published</span>
                        </div>
                    </div>
                    <div className="admin-stat">
                        <div className="admin-stat-icon"><i className="fas fa-pen-to-square"></i></div>
                        <div className="admin-stat-info">
                            <strong>{stats.draft}</strong>
                            <span>Drafts</span>
                        </div>
                    </div>
                    <div className="admin-stat">
                        <div className="admin-stat-icon"><i className="fas fa-image"></i></div>
                        <div className="admin-stat-info">
                            <strong>{stats.withImage}</strong>
                            <span>With Image</span>
                        </div>
                    </div>
                </div>

                {/* Workspace: Form + Live Preview */}
                <div className="admin-workspace">
                    {/* Form */}
                    <div className="admin-panel">
                        <div className="admin-panel-header">
                            <h3>
                                <i className={form.id ? 'fas fa-pen-to-square' : 'fas fa-circle-plus'}></i>
                                {form.id ? 'Edit Record' : 'Create New Record'}
                            </h3>
                            <div className="admin-status-toggle">
                                <button
                                    type="button"
                                    className={form.status === 'published' ? 'active' : ''}
                                    onClick={() => handleStatus('published')}
                                >Published</button>
                                <button
                                    type="button"
                                    className={form.status === 'draft' ? 'active draft' : ''}
                                    onClick={() => handleStatus('draft')}
                                >Draft</button>
                            </div>
                        </div>

                        <div className="admin-panel-body">
                            <form className="admin-form" onSubmit={handleSave}>
                                <div className="admin-field">
                                    <label>Title <span className="admin-required">*</span></label>
                                    <input
                                        name="title"
                                        type="text"
                                        value={form.title}
                                        onChange={handleFieldChange}
                                        required
                                        placeholder="Enter a clear, attractive title"
                                    />
                                </div>

                                <div className="admin-field">
                                    <label>URL Slug</label>
                                    <input
                                        name="slug"
                                        type="text"
                                        value={form.slug}
                                        onChange={handleSlugChange}
                                        placeholder="auto-generated-from-title"
                                    />
                                    <span className="admin-field-hint">
                                        Lowercase, hyphens only. Auto-generated from title.
                                    </span>
                                </div>

                                {/* Image upload */}
                                <div className="admin-field">
                                    <label>Featured Image</label>
                                    <div className={`admin-upload ${uploading ? 'uploading' : ''}`}>
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            onChange={handleImageUpload}
                                            disabled={uploading}
                                        />
                                        <div className="admin-upload-label">
                                            <i className={uploading ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-arrow-up'}></i>
                                            <strong>{uploading ? 'Uploading...' : 'Click or drop image here'}</strong>
                                            <span>JPG, PNG, WEBP, GIF · Max 5MB</span>
                                        </div>
                                        {form.image && (
                                            <div className="admin-upload-preview" onClick={(e) => e.stopPropagation()}>
                                                <img src={form.image} alt="Preview" />
                                            </div>
                                        )}
                                    </div>
                                    {form.image && (
                                        <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                                            <span className="admin-field-hint" style={{ flex: 1, wordBreak: 'break-all' }}>
                                                {form.image}
                                            </span>
                                            <button type="button" className="admin-btn sm secondary" onClick={handleClearImage}>
                                                <i className="fas fa-xmark"></i> Clear
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Module-specific fields */}
                                <ModuleFields
                                    moduleKey={activeModule}
                                    form={form}
                                    onChange={handleFieldChange}
                                />

                                <div className="admin-field">
                                    <label>{activeModule === 'faqs' ? 'Answer' : 'Summary / Short Description'}</label>
                                    <textarea
                                        name="summary"
                                        rows={3}
                                        value={form.summary}
                                        onChange={handleFieldChange}
                                        placeholder={activeModule === 'faqs'
                                            ? 'Provide a clear, helpful answer to the question.'
                                            : 'A short engaging summary shown on cards and listings.'}
                                    />
                                </div>

                                {activeModule !== 'faqs' && (
                                    <div className="admin-field">
                                        <label>Full Content / Description</label>
                                        <textarea
                                            name="content"
                                            rows={7}
                                            value={form.content}
                                            onChange={handleFieldChange}
                                            placeholder="Write the full description here. You can use multiple paragraphs — they will be preserved on the website."
                                        />
                                        <span className="admin-field-hint">
                                            This is the main body text shown on the detail page.
                                        </span>
                                    </div>
                                )}

                                <div className="admin-form-actions">
                                    <button type="submit" className="admin-btn" disabled={saving || uploading}>
                                        {saving ? (
                                            <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                                        ) : form.id ? (
                                            <><i className="fas fa-floppy-disk"></i> Update Record</>
                                        ) : (
                                            <><i className="fas fa-plus"></i> Create Record</>
                                        )}
                                    </button>
                                    <button type="button" className="admin-btn secondary" onClick={resetForm}>
                                        <i className="fas fa-rotate-left"></i> Reset
                                    </button>
                                </div>
                            </form>

                            {message && (
                                <div className="admin-toast success">
                                    <i className="fas fa-circle-check"></i> {message}
                                </div>
                            )}
                            {error && (
                                <div className="admin-toast error">
                                    <i className="fas fa-circle-exclamation"></i> {error}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Live Preview */}
                    <div className="admin-panel">
                        <div className="admin-panel-header">
                            <h3>
                                <i className="fas fa-display"></i>
                                Live Preview
                            </h3>
                            <span className="admin-pill published">
                                {form.status === 'draft' ? 'DRAFT' : 'PUBLISHED'}
                            </span>
                        </div>
                        <div className="admin-panel-body">
                            <PreviewPane form={form} moduleType={activeModuleDef.previewType} />
                        </div>
                    </div>
                </div>

                {/* Records list */}
                <div className="admin-panel">
                    <div className="admin-panel-header">
                        <h3>
                            <i className="fas fa-list"></i>
                            All {activeModuleDef.label}
                        </h3>
                        <span style={{ fontSize: 12, color: '#6E7E85' }}>
                            {loading ? 'Loading...' : `${items.length} record${items.length === 1 ? '' : 's'}`}
                        </span>
                    </div>
                    <div className="admin-panel-body">
                        {loading ? (
                            <div className="admin-records-empty">
                                <div className="admin-spinner" style={{ margin: '0 auto 10px' }}></div>
                                Loading records...
                            </div>
                        ) : items.length === 0 ? (
                            <div className="admin-records-empty">
                                <i className="fas fa-inbox"></i>
                                No records yet. Create your first {activeModuleDef.label.toLowerCase()} above.
                            </div>
                        ) : (
                            <div className="admin-records-list">
                                {items.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`admin-record ${form.id === item.id ? 'editing' : ''}`}
                                    >
                                        <div className="admin-record-thumb">
                                            {item.image
                                                ? <img src={item.image} alt={item.title} />
                                                : <i className={activeModuleDef.icon}></i>}
                                        </div>
                                        <div className="admin-record-info">
                                            <strong>{item.title}</strong>
                                            <span>/{item.slug}</span>
                                            <div className="admin-record-meta">
                                                <span className={`admin-pill ${item.status === 'published' ? 'published' : 'draft'}`}>
                                                    {item.status}
                                                </span>
                                                {item.updated_at && (
                                                    <span style={{ fontSize: 11, color: '#6E7E85' }}>
                                                        Updated {item.updated_at}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="admin-record-actions">
                                            <button
                                                type="button"
                                                className="admin-icon-btn"
                                                title="Edit"
                                                onClick={() => handleEdit(item)}
                                            >
                                                <i className="fas fa-pen"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="admin-icon-btn danger"
                                                title="Delete"
                                                onClick={() => handleDelete(item.id, item.title)}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

export default AdminDashboard;
